<script>
import substanceDictionaries from "@core/mixins/substance-dictionaries";
import { BRow, BCol, BFormGroup } from 'bootstrap-vue'
import {
    AdditionalMaterial, MaterialCategory, MixtureCategory, ConcentrationRange, Exemption
} from "@/views/product/components/product-card/composition/components";
import {ProductPropertyType} from "@core/domain/substance";

export default {
    mixins: [substanceDictionaries],
    components: {
        Exemption, BRow, BCol, BFormGroup, AdditionalMaterial, MaterialCategory, MixtureCategory, ConcentrationRange,
    },
    props: {
        properties: {
            type: Object,
            required: true,
        },
        values: {
            type: Object,
            required: true,
        },
        substance: {
            type: Object,
            required: true,
        },
        readOnly: {
            default: false,
        },
        compactView: {
            default: false
        },
        candidateList: {
            required: true,
            type: Object
        },
        isMaterialCategoryMandatory: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            treeselectMandatoryClass: 'treeselect-bg-warning',

            concentrationRange: this.getConcentrationRange(),
            materialCategory: this.getMaterialCategory(),
            additionalMaterial: this.getAdditionalMaterial(),
            mixtureCategory: this.getMixtureCategory(),
            exemption: this.getExemption(),

            types: ProductPropertyType
        };
    },
    methods: {
        getExemption() {
            if (this.values.hasOwnProperty(ProductPropertyType.EXEMPTION)) {
                const container = this.values[ProductPropertyType.EXEMPTION].find(item => item.list.id === this.candidateList.id);
                if (container) {
                    return container.value;
                }
            }
            return [];
        },
        getAdditionalMaterial() {
            return this.values[ProductPropertyType.ADDITIONAL_MATERIAL];
        },
        getMixtureCategory() {
            return this.values[ProductPropertyType.MIXTURE_CATEGORY];
        },
        getMaterialCategory() {
            return this.values[ProductPropertyType.MATERIAL_CATEGORY];
        },
        getConcentrationRange() {
            return this.values[ProductPropertyType.MAX_CONCENTRATION_RANGE];
        },

        onMaterialCategoryChange(value) {
            this.materialCategory = value;
            this.emit();
        },

        onExemptionSelect(exemption) {
            this.exemption = exemption;
            this.emit();
        },

        onMixtureCategoryChange(value) {
            this.mixtureCategory = value;
            this.emit();
        },

        onAdditionalMaterialChange(value) {
            this.additionalMaterial = value;
            this.emit();
        },

        onConcentrationRangeChange(value) {
            this.concentrationRange = value;
            this.emit();
        },

        emit() {
            const data = {};
            data[ProductPropertyType.EXEMPTION] = this.exemption;
            data[ProductPropertyType.MAX_CONCENTRATION_RANGE] = this.concentrationRange;
            data[ProductPropertyType.ADDITIONAL_MATERIAL] = this.additionalMaterial;
            data[ProductPropertyType.MATERIAL_CATEGORY] = this.materialCategory;
            data[ProductPropertyType.MIXTURE_CATEGORY] = this.mixtureCategory;

            this.$emit('input', data);
        },
    },
    computed: {
        applyWarningClassForMixtureOrMaterial() {
            if (this.materialCategory && this.materialCategory.length > 0) {
                return false;
            }
            return !(this.mixtureCategory && this.mixtureCategory.length > 0);
        },
        concentrationRangeOptions() {
            return this.candidateList.concentrationRanges;
        },
        listExemptions() {
            return this.getExemption();
        },
        additionalMaterialValues() {
            return this.getAdditionalMaterial();
        }
    },
    watch: {
        values() {
            this.concentrationRange = this.getConcentrationRange();
            this.materialCategory = this.getMaterialCategory();
            this.additionalMaterial = this.getAdditionalMaterial();
            this.mixtureCategory = this.getMixtureCategory();
            this.exemption = this.getExemption();
        },
        additionalMaterialValues() {
            this.additionalMaterial = this.getAdditionalMaterial();
        },
        'values.mixture_category': function () {
            this.mixtureCategory = this.getMixtureCategory();
        },
        'values.material_category': function () {
            this.materialCategory = this.getMaterialCategory();
        },
        'values.max_concentration_range': function () {
            this.concentrationRange = this.getConcentrationRange();
        },
        listExemptions() {
            this.exemption = this.getExemption();
        },
    }
}
</script>

<template>
    <div>
        <b-row v-if="compactView">
            <b-col>
                <ConcentrationRange
                    :read-only="readOnly"
                    :value="concentrationRange"
                    :config="{data: concentrationRangeOptions}"
                    @input="onConcentrationRangeChange"
                />
                <MixtureCategory
                    :read-only="readOnly"
                    :config="properties[types.MIXTURE_CATEGORY]"
                    :value="mixtureCategory"
                    :apply-warning-class="applyWarningClassForMixtureOrMaterial"
                    @input="onMixtureCategoryChange"
                />
                <MaterialCategory
                    :read-only="readOnly"
                    :apply-warning-class="applyWarningClassForMixtureOrMaterial"
                    :config="properties[types.MATERIAL_CATEGORY]"
                    :value="materialCategory"
                    @input="onMaterialCategoryChange"
                />
                <AdditionalMaterial
                    :read-only="readOnly"
                    :config="properties[types.ADDITIONAL_MATERIAL]"
                    :value="additionalMaterial"
                    @input="onAdditionalMaterialChange"
                />
            </b-col>
        </b-row>
        <b-row v-else>
            <b-col md="5" lg="5">
                <ConcentrationRange
                    :read-only="readOnly"
                    :value="concentrationRange"
                    :config="{data: concentrationRangeOptions}"
                    @input="onConcentrationRangeChange"
                />
                <MixtureCategory
                    :read-only="readOnly"
                    :apply-warning-class="applyWarningClassForMixtureOrMaterial"
                    :config="properties[types.MIXTURE_CATEGORY]"
                    :value="mixtureCategory"
                    @input="onMixtureCategoryChange"
                />
            </b-col>
            <b-col md="7" lg="7">
                <MaterialCategory
                    :read-only="readOnly"
                    :apply-warning-class="applyWarningClassForMixtureOrMaterial"
                    :config="properties[types.MATERIAL_CATEGORY]"
                    :value="materialCategory"
                    @input="onMaterialCategoryChange"
                />
                <AdditionalMaterial
                    :read-only="readOnly"
                    :config="properties[types.ADDITIONAL_MATERIAL]"
                    :value="additionalMaterial"
                    @input="onAdditionalMaterialChange"
                />
            </b-col>
        </b-row>
        <Exemption
            :read-only="readOnly"
            :value="exemption"
            @input="onExemptionSelect"
        />
    </div>
</template>
